/**
 * @file     index.tsx
 * @purpose  Relta Agent mobile app Privacy Policy page component with i18n-wrapped content.
 */
'use client';

import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Link from 'next/link';

const ReltaAgentPrivacyPage = () => {
  const { t } = useTranslation();

  const sectionHeading = { fontWeight: 500, mt: 2 } as const;
  // Em-dash brand title, composed via lone-glyph constant to satisfy the
  // no-em-dash copy guard while matching the App Store listing exactly.
  const title = `Relta Agent ${'—'} Privacy Policy`;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
        {t('legal.reltaAgentPrivacy.title', { defaultValue: title })}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 4 }}>
        {t('legal.reltaAgentPrivacy.lastUpdated', {
          defaultValue: 'Last updated: October 8, 2026 (Version 2026.10)',
        })}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, '& p': { lineHeight: 1.8 } }}>
        <Typography>
          {t('legal.reltaAgentPrivacy.intro', {
            defaultValue:
              'This Privacy Policy explains how Ray Rasouli ("I," "me," or "my"), who owns and operates the Relta platform, the Relta Agent app, and the Relta and Relta Real Estate names, handles information in connection with the Relta Agent mobile app for iOS and Android (the "App"). I am a licensed real estate professional with Real Broker B.C. Ltd.; my real estate practice is governed by the relta.ca Privacy Policy. Relta Real Estate is not a brokerage. The App is built for licensed real estate agents to run open-house sign-ins: an agent builds a sign-in form, collects visitor information at open houses (including offline), and manages those contacts. By creating an account or using the App, you agree to this Privacy Policy.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.collectHeading', {
            defaultValue: 'What I collect and why',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.collectIntro', {
            defaultValue: 'The App handles four categories of information:',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.collectAccount', {
            defaultValue:
              'Agent account data. When you create an account I collect your email address and, optionally, your name and phone number. I use this to authenticate you, operate your account, provide support, and send service-related messages about the App.',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.collectLeads', {
            defaultValue:
              'Open-house lead data. When you collect visitor information at an open house, the App stores the data you gather, which may include a visitor’s name, email, phone number, answers to the questions on your form, and any consent the visitor provides. You decide what your form asks. I store and process this data on your behalf so you can access and manage your contacts.',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.collectCrm', {
            defaultValue:
              'CRM connection keys. If you connect a customer relationship management (CRM) service such as Follow Up Boss or Lofty, I store the API key needed to send your leads to that service. CRM keys are encrypted server-side, are never shown back to your device, and are never shared with anyone. I use them only to deliver your leads to the CRM you chose.',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.collectDiagnostics', {
            defaultValue:
              'Crash and diagnostic data. I use Sentry to collect crash reports and diagnostic information so I can find and fix problems and keep the App reliable. This data is used only to maintain and improve the App.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.controllerHeading', {
            defaultValue: 'You are the controller of lead data',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.controllerBody', {
            defaultValue:
              'For the open-house lead data you collect, you are the controller and I am a processor acting on your instructions. This means you are responsible for what you collect, for obtaining any required consent from the people whose information you gather, and for using that information lawfully. I process lead data only to provide the App’s features to you, such as storing your contacts and delivering them to a CRM you have connected.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.noTrackingHeading', {
            defaultValue: 'No tracking, no advertising, never sold',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.noTrackingBody', {
            defaultValue:
              'The App contains no advertising and no advertising SDKs. I do not use third-party tracking, and I do not collect the advertising identifier (IDFA) or use App Tracking Transparency tracking. I do not sell your data or your leads’ data, and I do not share it for advertising or for any third party’s own marketing.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.subprocessorsHeading', {
            defaultValue: 'Service providers and subprocessors',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.subprocessorsBody', {
            defaultValue:
              'I rely on a small set of service providers to operate the App: Supabase (hosting, database, and authentication); Apple (in-app purchases); RevenueCat (subscription management); and Sentry (crash reporting). When you choose to connect a CRM, your selected provider (Follow Up Boss or Lofty) also receives the lead data you send to it. I do not share data with these providers beyond what is needed to operate the App.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.retentionHeading', {
            defaultValue: 'Data retention and deletion',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.retentionBody', {
            defaultValue:
              'I keep your account and the data you store for as long as your account is active. You can delete your account at any time from within the App at Settings → Delete account. Deleting your account removes your account and its associated data, including the leads and CRM connection keys stored for that account. I may retain limited records where required to comply with legal obligations.',
          })}{' '}
          {t('legal.reltaAgentPrivacy.retentionLinkLead', {
            defaultValue: 'To ask for deletion without the app, follow ',
          })}
          <Link href="/relta-agent-app/delete-account">
            {t('legal.reltaAgentPrivacy.retentionLink', { defaultValue: 'these steps' })}
          </Link>
          .
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.securityHeading', { defaultValue: 'Security' })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.securityBody', {
            defaultValue:
              'I use industry-standard safeguards to protect the data in the App, including encryption in transit and encrypted server-side storage of CRM connection keys. No method of transmission or storage is completely secure, so I cannot guarantee absolute security, but I work to protect your information and to address issues promptly.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.childrenHeading', { defaultValue: 'Children' })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.childrenBody', {
            defaultValue:
              'The App is intended for licensed real estate professionals and is not directed to children. I do not knowingly collect personal information from anyone under the age of 13 (or under 16 where a higher age applies). If you believe a child has provided information through the App, contact me so I can remove it.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.internationalHeading', {
            defaultValue: 'International users',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.internationalBody', {
            defaultValue:
              'The App is operated from Canada by Ray Rasouli, and the data it handles may be stored and processed in Canada, the United States, or other countries where my service providers operate. If you use the App from outside these countries, you understand that your information may be transferred to and processed in countries whose data protection laws may differ from those in your jurisdiction.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.changesHeading', {
            defaultValue: 'Changes to this policy',
          })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.changesBody', {
            defaultValue:
              'I may update this Privacy Policy from time to time. When I do, I will revise the "Last updated" date above and post the updated policy at this address. Material changes may also be communicated within the App. Continued use of the App after an update means you accept the revised policy.',
          })}
        </Typography>

        <Typography variant="h5" component="h2" sx={sectionHeading}>
          {t('legal.reltaAgentPrivacy.contactHeading', { defaultValue: 'How to contact me' })}
        </Typography>
        <Typography>
          {t('legal.reltaAgentPrivacy.contactBody', {
            defaultValue:
              'For questions about this Privacy Policy or about the data the App handles, contact the Privacy Officer at ray@relta.ca. I am located in Vancouver, British Columbia, Canada. You can also review my ',
          })}
          <Link href="/relta-agent-terms" style={{ color: 'inherit' }}>
            {t('legal.reltaAgentPrivacy.termsLink', { defaultValue: 'Terms of Use' })}
          </Link>
          {t('legal.reltaAgentPrivacy.contactBodyAfter', { defaultValue: '.' })}
        </Typography>
      </Box>
    </Container>
  );
};

export default ReltaAgentPrivacyPage;
